'use client';

import classnames from 'classnames';
import { ArticleNavigator, ProgressBar } from '@stadionhq-v3/base-ui';
import ArticleHeader from '@components/v3/organisms/article-headers/articleHeader.component';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Tag } from '@components/v3/atoms/tag';
import styles from './article.module.scss';
import { ArticleProps } from './article.types';

const Article = ({
  nativeProps,
  classNames,
  title,
  publishDate,
  category,
  image,
  summary,
  author,
  estimatedReadingTime,
  bodyCopy,
  tags,
  sponsor,
  teamIndicator,
  isArticleDark = false,
  isArticleHeaderDark,
  format,
  articleHeaderShareTray,
  previousArticleLink,
  nextArticleLink,
  featuredGallery,
  featuredVideo,
  membersBadge,
  progressBar,
  contain = true,
}: ArticleProps) => {
  const classes = classnames(
    [
      styles.tokens,
      styles.article,
      styles[format.toLowerCase()],
      contain && styles.contain,
    ],
    {
      [`${styles.onDark}`]: isArticleDark,
    },
    classNames
  );

  const articleRef = useRef<HTMLDivElement>(null);

  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    // if (!subscriptionBarrier && !progressBar) return undefined;
    if (!progressBar) return undefined;

    const handleScroll = () => {
      if (articleRef.current) {
        const articleHeight = articleRef.current.scrollHeight;
        const scrollPosition = window.scrollY + window.innerHeight;

        const progress = (scrollPosition * 100) / articleHeight;

        setScrollProgress(progress);

        // const triggerSubscriptionBarrier =
        //   window.scrollY + window.innerHeight > window.innerHeight;

        // if (triggerSubscriptionBarrier && subscriptionBarrier) {
        //   setSubscriptionBarrierVisible(!!subscriptionBarrier);
        // }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // }, [subscriptionBarrier, progressBar]);
  }, [progressBar]);

  return (
    <div
      ref={articleRef}
      {...nativeProps}
      className={classes}
      data-component="Article"
    >
      {(nextArticleLink || previousArticleLink) && (
        <div className={classnames(styles.articleNavigatorContainer)}>
          <ArticleNavigator
            {...{ previousArticleLink, nextArticleLink }}
            classNames={[classnames(styles.articleNavigator)]}
            onDark={isArticleHeaderDark}
          />

          {progressBar && (
            <ProgressBar
              progress={scrollProgress}
              {...progressBar}
              classNames={[
                classnames(styles.progressBar, progressBar.classNames),
              ]}
            />
          )}
        </div>
      )}

      <ArticleHeader
        type={format}
        title={title}
        publishDate={publishDate}
        category={category}
        image={image}
        summary={summary}
        author={author}
        estimatedReadingTime={estimatedReadingTime}
        sponsor={sponsor}
        teamIndicator={teamIndicator}
        onDark={isArticleHeaderDark}
        shareTray={articleHeaderShareTray}
        featuredVideo={featuredVideo}
        membersBadge={membersBadge}
        contain={contain}
      />

      <div className={styles.articleBody}>
        <div className={classnames(styles.contentContainer)}>
          <div className={styles.articleContent}>
            {featuredGallery && featuredGallery}

            <div className={classnames([styles.bodyCopy])}>{bodyCopy}</div>
          </div>

          <div className={styles.lowerContent}>
            {tags && (
              <div className={styles.tagBlock}>
                {tags.title && (
                  <div className={styles.tagBlockTitle}>{tags.title}</div>
                )}

                <div className={styles.tags}>
                  {tags.tags.map((tag) => (
                    <Fragment key={tag.id}>
                      <Tag {...tag} onDark={isArticleDark} />
                    </Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Article;
